import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Activity Document (matches backend ActivityDocument : BaseDocument) ---

export interface Activity {
  id: string;
  tenant_id: string;
  type: string;
  subject: string;
  content: string;
  related_to_type: string;
  related_to_id: string;
  scheduled_at: string | null;
  completed_at: string | null;
  duration: number;
  owner_id: string;
  status: string;
  notes: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs ---

export interface CreateActivityRequest {
  type: string;
  subject: string;
  content?: string;
  related_to_type: string;
  related_to_id: string;
  scheduled_at?: string;
  completed_at?: string;
  duration?: number;
  owner_id?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateActivityRequest {
  type: string;
  subject: string;
  content?: string;
  related_to_type: string;
  related_to_id: string;
  scheduled_at?: string;
  completed_at?: string;
  duration?: number;
  owner_id?: string;
  status?: string;
  notes?: string;
  tags?: string[];
}

// --- Filter params for activity list ---

export interface ActivityFilters {
  type?: string;
  related_to_type?: string;
  related_to_id?: string;
  owner_id?: string;
  status?: string;
}

// --- API functions ---

const BASE = '/activities';

export async function listActivities(
  params: SearchParams,
  filters?: ActivityFilters
): Promise<PagedResponse<Activity>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.type) query.type = filters.type;
  if (filters?.related_to_type) query.related_to_type = filters.related_to_type;
  if (filters?.related_to_id) query.related_to_id = filters.related_to_id;
  if (filters?.owner_id) query.owner_id = filters.owner_id;
  if (filters?.status) query.status = filters.status;

  const response = await apiGet<PagedResponse<Activity>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getActivity(id: string): Promise<Activity> {
  const response = await apiGet<Activity>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Activity not found');
  }
  return response.data;
}

export async function createActivity(data: CreateActivityRequest): Promise<Activity> {
  const response = await apiPost<Activity>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create activity');
  }
  return response.data;
}

export async function updateActivity(id: string, data: UpdateActivityRequest): Promise<Activity> {
  const response = await apiPut<Activity>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update activity');
  }
  return response.data;
}

export async function deleteActivity(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}
